import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  // Base styles: modern button with subtle gradients and borders
  "inline-flex items-center brightness-100 justify-center whitespace-nowrap relative rounded-md text-sm font-normal focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 box-border border-[0.5px] border-transparent btn-safari-fix disabled:brightness-100 hover:brightness-[0.8] active:brightness-[0.65] after:border-[0.5px] after:border-black/20 dark:after:border-white/10 before:inset-0 before:border-t-[0.5px] before:border-white/20 before:h-full before:w-full before:absolute before:rounded-[inherit] before:bg-gradient-to-b before:from-white/[0.03] before:to-black/[0.03] before:pointer-events-none dark:before:top-[-0.5px] dark:before:w-[calc(100%+1px)] dark:before:start-[-0.5px] dark:before:end-[-0.5px] after:absolute after:inset-[-0.5px] after:rounded-[inherit] after:pointer-events-none after:-z-10 after:transition-colors after:duration-200 after:ease-out after:shadow-sm isolate transition-all duration-350 ease-out-smooth",
  {
    variants: {
      variant: {
        // Primary CTA - Bold blue
        default: "bg-primary text-primary-foreground ring-black/[0.04] dark:ring-black/[0.16]",
        // Secondary - Dark charcoal
        secondary: "bg-secondary text-secondary-foreground ring-black/[0.04] dark:ring-black/[0.16]",
        // Outline - Clear border
        outline: "border-2 border-foreground/20 bg-transparent text-foreground hover:bg-foreground/5",
        // Ghost - Subtle but visible
        ghost: "text-foreground hover:bg-foreground/10",
        // Destructive
        destructive: "bg-destructive text-destructive-foreground ring-black/[0.04] dark:ring-black/[0.16]",
        // Link
        link: "text-primary underline-offset-4 hover:underline before:hidden after:hidden",
        // CTA variant - Primary blue
        cta: "bg-primary text-primary-foreground ring-black/[0.08] dark:ring-black/20 hover:bg-primary/90",
        // CTA White variant
        ctaWhite: "bg-white text-foreground ring-black/[0.08] dark:ring-white/[0.16] hover:bg-white/90 border border-border",
        // Skew variant - Dark with skew
        skew: "bg-secondary text-secondary-foreground ring-black/[0.04] dark:ring-black/[0.16] sm:skew-x-[-3deg] sm:[&>*]:skew-x-[3deg]",
      },
      size: {
        sm: "h-7 px-3 pt-[7px] pb-[5px] gap-1.5 has-[>svg]:px-2.5 [&_svg:not([class*='size-'])]:size-3.5",
        default: "h-9 px-4 pt-[9px] pb-[7px] gap-2 has-[>svg]:px-3 [&_svg:not([class*='size-'])]:size-4",
        lg: "h-11 px-6 pt-[11px] pb-[9px] gap-2.5 has-[>svg]:px-4 [&_svg:not([class*='size-'])]:size-5",
        icon: "h-9 w-9 gap-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
